"use client";
import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Chip,
} from "@mui/material";
import Header from "./header";
import Footer from "./footer";
import Seo from "./components/Seo";
import seoRoutes from "../seo/seoRoutes";
import {
  headerLikeBg,
  heroSx,
  titleGradient,
  ORANGE_ACCENT,
} from "./components/ui/themeBits";

// ------- Sample Fee Data (edit amounts as needed) -------
const FEES = {
  "Class 11": [
    {
      stream: "Science",
      heads: {
        Tuition: 9000,
        Admission: 1500,
        Lab: 0,
        Library: 0,
        Lab: 0,
        Sports: 0,
        "Exam Fee": "As Per JAC",
      },
    },
    {
      stream: "Commerce",
      heads: {
        Tuition: 6000,
        Admission: 1500,
        Library: 0,
        Lab: 0,
        Sports: 0,
        "Exam Fee": "As Per JAC",
      },
    },
    {
      stream: "Arts",
      heads: {
        Tuition: 6000,
        Admission: 1500,
        Library: 0,
        Lab: 0,
        Sports: 0,
        "Exam Fee": "As Per JAC",
      },
    },
  ],
  "Class 12": [
    {
      stream: "Science",
      heads: {
        Tuition: 9000,
        // Admission: 2000,
        Lab: 0,
        Library: 0,
        Sports: 0,
        "Exam Fee": "As Per JAC",
      },
    },
    {
      stream: "Commerce",
      heads: {
        Tuition: 6000,
        // Admission: 2000,
        Library: 0,
        Lab: 0,
        Sports: 0,
        "Exam Fee": "As Per JAC",
      },
    },
    {
      stream: "Arts",
      heads: {
        Tuition: 6000,
        // Admission: 2000,
        Library: 0,
        Lab: 0,
        Sports: 0,
        "Exam Fee": "As Per JAC",
      },
    },
  ],
};

// ------- helpers -------
const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);

// NEW: how to show individual head values
const formatHeadDisplay = (raw) => {
  if (raw === undefined || raw === null) {
    // head not applicable for this stream
    return "—";
  }
  if (raw === "As Per JAC") {
    // head not applicable for this stream
    return "As Per JAC";
  }

  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return "—";
  }
  if (n === 0) {
    // specifically mark zero-amount as Free
    return "Included";
  }
  return formatINR(n);
};

const computeTotal = (heads) =>
  Object.values(heads).reduce((sum, v) => sum + (Number(v) || 0), 0);

// export const metadata = {
//   title: "Fee Structure | AISECT Inter College",
//   description:
//     "Annual fee structure for Intermediate (Class 11 & 12) — Science, Commerce, and Arts streams.",
//   openGraph: { title: "Fee Structure | AISECT Inter College" },
// };

export default function FeeStructureClientPage() {
  // const [tab, setTab] = 1;
  // const [tab, setTab] = React.useState<"Class 11" | "Class 12">("Class 11");
  // const [filter, setFilter] = React.useState<"All" | "Science" | "Commerce" | "Arts">("All"); // All | Science | Commerce | Arts
const [tab, setTab] = React.useState("Class 11");
const [filter, setFilter] = React.useState("All");
  const streams = FEES[tab] || [];
  const visible = streams.filter((s) =>
    filter === "All" ? true : s.stream === filter
  );

  // union of all fee heads (rows)
  const allHeads = Array.from(
    new Set(streams.flatMap((s) => Object.keys(s.heads)))
  );

  const grandTotal = visible.reduce(
    (sum, s) => sum + computeTotal(s.heads),
    0
  );

 const seo = seoRoutes["/feestructure"];

return (
  <>
    <Seo
      title={seo.title}
      description={seo.description}
      path="/feestructure"
      image={seo.image}
    />
      <Header />

      {/* HERO */}
      <Box sx={heroSx}>
        <Container maxWidth="lg">
          <Typography component="h1" align="center" sx={titleGradient}>
            Fee Structure
          </Typography>
          <Typography
            align="center"
            sx={{
              mt: 1,
              color: "text.secondary",
              fontSize: { xs: 15, sm: 16 },
            }}
          >
            Intermediate (Class 11 & 12) — Science, Commerce, and Arts
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ my: { xs: 3.5, sm: 5 } }}>
        {/* Controls */}
        <Card
          elevation={0}
          sx={{ ...headerLikeBg, borderRadius: 3, p: { xs: 1.5, sm: 2 } }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
            justifyContent="space-between"
          >
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{
                "& .MuiTabs-indicator": { backgroundColor: ORANGE_ACCENT },
                "& .MuiTab-root": {
                  fontWeight: 800,
                  textTransform: "none",
                  fontSize: "clamp(14px, 1.8vw, 18px)", // bigger & responsive
                  minHeight: 44,
                },
              }}
            >
              <Tab value="Class 11" label="Class 11" />
              <Tab value="Class 12" label="Class 12" />
            </Tabs>

            <ToggleButtonGroup
              color="primary"
              exclusive
              value={filter}
              onChange={(_, v) => v && setFilter(v)}
              size="small"
              sx={{
                alignSelf: { xs: "flex-end", sm: "auto" },
                "& .MuiToggleButton-root": {
                  px: { xs: 1.25, sm: 1.75 },
                  py: { xs: 0.5, sm: 0.75 },
                  fontWeight: 700,
                  fontSize: "clamp(13px, 1.6vw, 16px)", // responsive
                },
                "& .MuiToggleButton-root.Mui-selected": {
                  backgroundColor: ORANGE_ACCENT,
                  color: "#fff",
                },
              }}
            >
              <ToggleButton value="All">All</ToggleButton>
              <ToggleButton value="Science">Science</ToggleButton>
              <ToggleButton value="Commerce">Commerce</ToggleButton>
              <ToggleButton value="Arts">Arts</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Card>

        {/* Table */}
        <Card
          elevation={0}
          sx={{
            mt: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            ...headerLikeBg,
          }}
        >
          <CardContent sx={{ p: { xs: 1.25, sm: 2 } }}>
            <Box sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 900,
                        fontSize: "clamp(15px, 2vw, 18px)", // bigger & responsive
                      }}
                    >
                      Fee Head
                    </TableCell>
                    {visible.map((s) => (
                      <TableCell
                        key={s.stream}
                        align="right"
                        sx={{
                          fontWeight: 900,
                          fontSize: "clamp(15px, 2vw, 18px)", // bigger & responsive
                        }}
                      >
                        {s.stream}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allHeads.map((head) => (
                    <TableRow key={head}>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: "clamp(14px, 1.9vw, 17px)", // larger labels
                        }}
                      >
                        {head}
                      </TableCell>
                      {visible.map((s) => {
                        const value = s.heads[head];
                        return (
                          <TableCell
                            key={s.stream + head}
                            align="right"
                            sx={{
                              fontSize: "clamp(14px, 1.9vw, 17px)", // larger values
                            }}
                          >
                            {formatHeadDisplay(value)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}

                  {/* totals */}
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 900,
                        fontSize: "clamp(15px, 2vw, 18px)",
                        borderTop: (t) => `2px solid ${t.palette.divider}`,
                      }}
                    >
                      Total (Annual)
                    </TableCell>
                    {visible.map((s) => (
                      <TableCell
                        key={s.stream + "_total"}
                        align="right"
                        sx={{
                          fontWeight: 900,
                          fontSize: "clamp(15px, 2vw, 18px)",
                          borderTop: (t) => `2px solid ${t.palette.divider}`,
                        }}
                      >
                        {formatINR(computeTotal(s.heads))}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </Box>

            {/* Summary strip */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              sx={{ mt: 2 }}
            >
               <Stack direction="row" spacing={1} flexWrap="wrap"> 
               {/* <Chip label="Annual fees" size="md" />
                <Chip label="Inclusive of exam fees" size="md" />
                <Chip label="Lab fee only for Science" size="md" />
                <Chip label="₹0 marked as Free" size="md" />*/}
              </Stack> 
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "clamp(15px, 2vw, 18px)", // emphasized & responsive
                }}
              >
                Grand Total (selected): {formatINR(grandTotal)}
              </Typography>
            </Stack>

            {/* Notes */}
            <Box sx={{ mt: 1.5 }}>
              <Typography
                sx={{ color: "text.secondary", fontSize: { xs: 13.5, sm: 14.5 } }}
              >
                * Transport, uniform, books, and optional activities are charged
                separately (if applicable). Fees are subject to revision as per
                management/board guidelines.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>

      <Footer />
    </>
  );
}
